import API_URL from "../config/config";

export const WEBSITE_NOTIFICATIONS_UPDATED_EVENT =
  "instaport:website-notifications-updated";

const NOTIFICATION_CACHE_TTL_MS = 15 * 1000;
const notificationsCache = new Map();
const notificationsInFlight = new Map();

export const emitWebsiteNotificationsUpdated = (detail = null) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(WEBSITE_NOTIFICATIONS_UPDATED_EVENT, { detail })
  );
};

const getHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

export const getCachedWebsiteNotifications = ({ token, userId }) => {
  const cached = notificationsCache.get(`${userId}:${token}`);

  if (!cached || Date.now() - cached.timestamp >= NOTIFICATION_CACHE_TTL_MS) {
    return null;
  }

  return cached.notifications;
};

export const cacheWebsiteNotifications = ({ token, userId, notifications }) => {
  notificationsCache.set(`${userId}:${token}`, {
    notifications,
    timestamp: Date.now(),
  });
};

export const fetchWebsiteNotifications = async ({
  token,
  userId,
  force = false,
}) => {
  const requestKey = `${userId}:${token}`;
  const cached = getCachedWebsiteNotifications({ token, userId });

  if (!force && cached) {
    return cached;
  }

  if (!force && notificationsInFlight.has(requestKey)) {
    return notificationsInFlight.get(requestKey);
  }

  const request = (async () => {
    const response = await fetch(
      `${API_URL}/notify?userId=${encodeURIComponent(
        userId
      )}&userType=USER`,
      {
        method: "GET",
        headers: getHeaders(token),
      }
    );

    let data = [];
    try {
      data = await response.json();
    } catch (_) {
      data = [];
    }

    if (!response.ok) {
      throw new Error(data?.message || "Failed to load notifications");
    }

    const notifications = Array.isArray(data) ? data : [];
    cacheWebsiteNotifications({ token, userId, notifications });
    return notifications;
  })();

  notificationsInFlight.set(requestKey, request);

  try {
    return await request;
  } finally {
    notificationsInFlight.delete(requestKey);
  }
};

export const markWebsiteNotificationRead = async ({
  token,
  notificationId,
}) => {
  const response = await fetch(`${API_URL}/notify/${notificationId}/read`, {
    method: "PUT",
    headers: getHeaders(token),
  });

  let data = null;
  try {
    data = await response.json();
  } catch (_) {
    data = null;
  }

  if (!response.ok) {
    throw new Error(data?.message || "Failed to mark notification as read");
  }

  return data;
};

export const markAllWebsiteNotificationsRead = async ({
  token,
  notifications,
}) => {
  const unreadNotifications = (notifications || []).filter(
    (notification) => !notification?.isRead && notification?._id
  );

  if (!unreadNotifications.length) {
    emitWebsiteNotificationsUpdated({ unreadCount: 0 });
    return;
  }

  await Promise.all(
    unreadNotifications.map((notification) =>
      markWebsiteNotificationRead({
        token,
        notificationId: notification._id,
      })
    )
  );

  emitWebsiteNotificationsUpdated({ unreadCount: 0 });
};
